import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/custom/Navbar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Search, Plus, MapPin, Calendar, Phone, Mail, Pill, Heart,
  Dog, Cat, HelpCircle, Download, Share2, CheckCircle, X,
  AlertTriangle, Clock, QrCode, Printer, Eye, Flag, Ban,
  MessageCircle, Navigation, Map,
} from "lucide-react";
import { SpeakButton } from "@/components/custom/SpeakButton";
import { ShareButtonsRow, ShareLostPetButton } from "@/components/custom/ShareButtons";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { C, DISPLAY, BODY, MONO } from "@/styles/editorial";

// ===== BRUTALIST STYLES =====
const B = {
  border: `1px solid ${C.navy}15`,
  shadow: "none",
  shadowSm: "none",
  font: BODY,
  mono: MONO,
};

function BCard({ children, className = "", style = {} }) {
  return (
    <div className={`bg-white ${className}`} style={{ border: B.border, boxShadow: B.shadow, ...style }}>
      {children}
    </div>
  );
}

function BBadge({ children, bg = "#686af3", color = "#fff" }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
      style={{ fontFamily: B.mono, background: bg, color, border: "2px solid #2D3561" }}
    >{children}</span>
  );
}

function BBtn({ children, onClick, disabled, variant = "primary", className = "", testId, style = {}, type = "button" }) {
  const styles = {
    primary: { background: "#e8637a", color: "#fff", border: B.border, boxShadow: B.shadowSm },
    secondary: { background: "#686af3", color: "#fff", border: B.border, boxShadow: B.shadowSm },
    success: { background: "#8B9A2D", color: "#fff", border: B.border, boxShadow: B.shadowSm },
    ghost: { background: "transparent", color: "#2D3561", border: "2px solid #ccc" },
    outline: { background: "#fff", color: "#2D3561", border: B.border },
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all duration-150 hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 ${className}`}
      style={{ fontFamily: B.mono, ...styles[variant], ...style }}
    >
      {children}
    </button>
  );
}

const inputStyle = { border: "2px solid #2D3561", borderRadius: 0 };
const selectStyle = { border: "2px solid #2D3561", borderRadius: 0 };
const dialogStyle = { borderRadius: 0, border: B.border, boxShadow: B.shadow };

// ===== CONFIG =====
const SPECIES_CONFIG = {
  perro: { icon: Dog, label: "Perro", color: "#f64e0e", emoji: "" },
  gato: { icon: Cat, label: "Gato", color: "#686af3", emoji: "" },
  otro: { icon: HelpCircle, label: "Otro", color: "#a88efb", emoji: "" },
};

const APPROACHABLE_CONFIG = {
  si: { label: "Sí, es amigable", color: "#8B9A2D", icon: "" },
  con_cuidado: { label: "Con cuidado", color: "#fa9000", icon: "" },
  no: { label: "No, puede huir/morder", color: "#e8637a", icon: "" },
};

export default function LostPetsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showPoster, setShowPoster] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [filter, setFilter] = useState({ status: "perdido", species: "all" });
  const [form, setForm] = useState({
    name: "", species: "perro", breed: "", color: "", characteristics: "",
    photo_url: "", lost_location_name: "", lost_location_lat: null,
    lost_location_lng: null, lost_date: "", lost_time: "", chip_number: "",
    medication: "", health_issues: "", approachable: "si",
    contact_phone: "", contact_email: "", additional_info: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sightings, setSightings] = useState([]);
  const [showSighting, setShowSighting] = useState(false);
  const [sightingForm, setSightingForm] = useState({
    description: "", location_lat: null, location_lng: null,
    location_name: "", photo_url: "",
  });
  const [locating, setLocating] = useState(false);
  const posterRef = useRef(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const token = localStorage.getItem("ea_token");
  const headers = { Authorization: `Bearer ${token}` };

  // Check if user is admin
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/check`, { headers }).then(res => {
      setIsAdmin(res.data.is_admin);
    }).catch(() => setIsAdmin(false));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status && filter.status !== "all") params.append("status", filter.status);
      if (filter.species && filter.species !== "all") params.append("species", filter.species);
      const res = await axios.get(`${API}/lost-pets?${params}`);
      setPets(res.data.pets || []);
    } catch (err) {
      toast.error("Error cargando datos");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadPets(); }, [loadPets]);

  const handleCreate = async () => {
    if (!user) { toast.error("Debes iniciar sesión para reportar"); navigate("/auth"); return; }
    if (!form.name || !form.photo_url || !form.lost_location_name || !form.lost_date || !form.contact_phone) {
      toast.error("Completa los campos obligatorios"); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/lost-pets`, form, { headers });
      toast.success("Reporte creado correctamente");
      setShowCreate(false);
      resetForm();
      loadPets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear reporte");
    } finally { setSubmitting(false); }
  };

  const resetForm = () => {
    setForm({
      name: "", species: "perro", breed: "", color: "", characteristics: "",
      photo_url: "", lost_location_name: "", lost_location_lat: null,
      lost_location_lng: null, lost_date: "", lost_time: "", chip_number: "",
      medication: "", health_issues: "", approachable: "si",
      contact_phone: "", contact_email: "", additional_info: "",
    });
  };

  const openPoster = (pet) => { setSelectedPet(pet); setShowPoster(true); loadSightings(pet.pet_id); };

  const loadSightings = async (petId) => {
    try {
      const res = await axios.get(`${API}/lost-pets/${petId}/sightings`);
      setSightings(res.data.sightings || []);
    } catch { setSightings([]); }
  };

  const handleCreateSighting = async () => {
    if (!user) { toast.error("Debes iniciar sesión"); navigate("/auth"); return; }
    if (!sightingForm.description || !sightingForm.location_lat) {
      toast.error("Describe lo que viste e indica la ubicación"); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/lost-pets/${selectedPet.pet_id}/sightings`, sightingForm, { headers });
      toast.success("Avistamiento reportado. ¡Gracias por ayudar!");
      setShowSighting(false);
      setSightingForm({ description: "", location_lat: null, location_lng: null, location_name: "", photo_url: "" });
      loadSightings(selectedPet.pet_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al reportar");
    } finally { setSubmitting(false); }
  };

  const handleReportSighting = async (sightingId) => {
    if (!user) { toast.error("Debes iniciar sesión"); return; }
    try {
      const res = await axios.post(`${API}/sightings/${sightingId}/report`, {}, { headers });
      toast.success(res.data.message);
      loadSightings(selectedPet.pet_id);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al reportar"); }
  };

  const geolocateSighting = () => {
    if (!navigator.geolocation) { toast.error("Geolocalización no disponible"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        let name = "";
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
          const addr = (await res.json()).address;
          name = [addr?.road, addr?.city || addr?.town || addr?.village].filter(Boolean).join(", ");
        } catch {}
        setSightingForm(f => ({ ...f, location_lat: lat, location_lng: lng, location_name: name || `${lat.toFixed(4)}, ${lng.toFixed(4)}` }));
        setLocating(false);
        toast.success(name ? `Ubicación: ${name}` : "Coordenadas guardadas");
      },
      () => { setLocating(false); toast.error("No se pudo obtener ubicación"); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const downloadPoster = async () => {
    if (!posterRef.current) return;
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(posterRef.current, { scale: 2, backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      link.download = `se-busca-${selectedPet.name}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success("Cartel descargado");
    } catch { toast.error("Error al descargar"); }
  };

  const markAsFound = async (petId) => {
    try {
      await axios.put(`${API}/lost-pets/${petId}/found`, {}, { headers });
      toast.success("¡Animal marcado como encontrado!");
      loadPets();
      setShowPoster(false);
    } catch { toast.error("Error al actualizar"); }
  };

  const publishToTelegram = async (petId) => {
    try {
      await axios.post(`${API}/admin/telegram/publish-lost-pet/${petId}`, {}, { headers });
      toast.success("¡Publicado en Telegram!");
      // Update the selected pet to show it's been published
      setSelectedPet(prev => prev ? { ...prev, telegram_published: true } : null);
      loadPets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error publicando en Telegram");
    }
  };

  const WHATSAPP_CHANNEL_URL = "https://whatsapp.com/channel/0029VbCWhL21t90VVBCzzp1U";

  const publishToWhatsApp = (pet) => {
    const speciesLabel = pet.species === "perro" ? "Perro" : pet.species === "gato" ? "Gato" : "Animal";
    const message = `*SE BUSCA: ${pet.name}*

${speciesLabel}${pet.breed ? ` - ${pet.breed}` : ''}
${pet.lost_location_name}
Perdido el ${new Date(pet.lost_date).toLocaleDateString('es-ES')}
Contacto: ${pet.contact_phone}

${pet.description || ''}

Más info: ${window.location.origin}/lost-pets?pet=${pet.pet_id}

¡Ayúdanos a encontrarlo!
#EmergenciaAnimal #Animales`;

    navigator.clipboard.writeText(message).then(() => {
      toast.success("¡Mensaje copiado! Pégalo en el canal de WhatsApp", { duration: 4000 });
      window.open(WHATSAPP_CHANNEL_URL, "_blank");
    }).catch(() => {
      toast.error("No se pudo copiar el mensaje");
    });
  };

  return (
    <div className="min-h-screen" data-testid="lost-pets-page" style={{ background: "#f8f7f4", fontFamily: B.font }}>
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-28 pb-16">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 flex items-center justify-center" style={{ background: "#e8637a", border: B.border }}>
              <Search className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: "#2D3561" }}>
                SE <span style={{ color: "#e8637a" }}>BUSCA</span>
              </h1>
              <p className="text-[10px] tracking-widest" style={{ fontFamily: B.mono, color: "#a88efb" }}>
                // AYÚDANOS A REUNIR FAMILIAS
              </p>
            </div>
          </div>
          <BBtn onClick={() => user ? setShowCreate(true) : navigate("/auth")} variant="primary" testId="report-lost-pet-btn">
            <Plus className="w-4 h-4" />
            REPORTAR PERDIDO
          </BBtn>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
            <SelectTrigger className="w-[140px]" style={selectStyle}>
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent style={{ borderRadius: 0, border: B.border }}>
              <SelectItem value="perdido">Perdidos</SelectItem>
              <SelectItem value="encontrado">Encontrados</SelectItem>
              <SelectItem value="all">Todos</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter.species} onValueChange={(v) => setFilter({ ...filter, species: v })}>
            <SelectTrigger className="w-[140px]" style={selectStyle}>
              <SelectValue placeholder="Especie" />
            </SelectTrigger>
            <SelectContent style={{ borderRadius: 0, border: B.border }}>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="perro">Perros</SelectItem>
              <SelectItem value="gato">Gatos</SelectItem>
              <SelectItem value="otro">Otros</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Pet Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3].map((i) => (
              <BCard key={i} className="p-4 animate-pulse">
                <div className="h-48 bg-gray-100 mb-4" style={{ border: "2px solid #2D3561" }} />
                <div className="h-4 bg-gray-100 mb-2" />
                <div className="h-3 bg-gray-100 w-2/3" />
              </BCard>
            ))}
          </div>
        ) : pets.length === 0 ? (
          <BCard className="text-center py-16 px-8">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center" style={{ background: "#f8f7f4", border: "2px solid #2D3561" }}>
              <Search className="w-10 h-10" style={{ color: "#ccc" }} />
            </div>
            <p className="text-lg font-bold" style={{ color: "#2D3561" }}>NO HAY REPORTES</p>
            <p className="text-[10px] tracking-widest mt-2" style={{ fontFamily: B.mono, color: "#a88efb" }}>
              // PRUEBA CON OTROS FILTROS O CREA UNO NUEVO
            </p>
          </BCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {pets.map((pet) => (
              <PetCard key={pet.pet_id} pet={pet} onOpenPoster={openPoster} />
            ))}
          </div>
        )}
      </div>

      {/* Create Report Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={dialogStyle}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: "#e8637a", border: "2px solid #2D3561" }}>
                <AlertTriangle className="w-4 h-4 text-white" />
              </div>
              REPORTAR ANIMAL PERDIDO
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>NOMBRE *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej: Luna" className="mt-1" style={inputStyle} data-testid="pet-name-input" />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>ESPECIE *</Label>
                <Select value={form.species} onValueChange={(v) => setForm({ ...form, species: v })}>
                  <SelectTrigger className="mt-1" style={selectStyle} data-testid="pet-species-select"><SelectValue /></SelectTrigger>
                  <SelectContent style={{ borderRadius: 0, border: B.border }}>
                    <SelectItem value="perro">Perro</SelectItem>
                    <SelectItem value="gato">Gato</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>RAZA</Label>
                <Input value={form.breed} onChange={(e) => setForm({ ...form, breed: e.target.value })} placeholder="Ej: Labrador" className="mt-1" style={inputStyle} />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>COLOR</Label>
                <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="Ej: Marrón con manchas" className="mt-1" style={inputStyle} />
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>URL DE LA FOTO *</Label>
              <Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://..." className="mt-1" style={inputStyle} data-testid="pet-photo-input" />
              <p className="text-[10px] mt-1" style={{ fontFamily: B.mono, color: "#a88efb" }}>
                // SUBE LA FOTO A IMGUR O GOOGLE DRIVE Y PEGA EL ENLACE
              </p>
            </div>

            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>CARACTERÍSTICAS</Label>
              <Textarea value={form.characteristics} onChange={(e) => setForm({ ...form, characteristics: e.target.value })} placeholder="Collar rojo, cicatriz, cojea..." className="mt-1" style={inputStyle} />
            </div>

            {/* Location & Date */}
            <div className="p-4" style={{ background: "#f8f7f4", border: "2px solid #2D3561" }}>
              <h4 className="text-xs font-bold tracking-widest mb-3" style={{ fontFamily: B.mono, color: "#e8637a" }}>DÓNDE Y CUÁNDO</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>ZONA *</Label>
                  <Input value={form.lost_location_name} onChange={(e) => setForm({ ...form, lost_location_name: e.target.value })} placeholder="Ej: Parque del Retiro, Madrid" className="mt-1" style={inputStyle} data-testid="pet-location-input" />
                </div>
                <div>
                  <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>FECHA *</Label>
                  <Input type="date" value={form.lost_date} onChange={(e) => setForm({ ...form, lost_date: e.target.value })} className="mt-1" style={inputStyle} data-testid="pet-date-input" />
                </div>
                <div>
                  <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>HORA</Label>
                  <Input type="time" value={form.lost_time} onChange={(e) => setForm({ ...form, lost_time: e.target.value })} className="mt-1" style={inputStyle} />
                </div>
              </div>
            </div>

            {/* Health Info */}
            <div className="p-4" style={{ background: "#f8f7f4", border: "2px solid #2D3561" }}>
              <h4 className="text-xs font-bold tracking-widest mb-3" style={{ fontFamily: B.mono, color: "#686af3" }}>SALUD</h4>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>Nº CHIP</Label>
                  <Input value={form.chip_number} onChange={(e) => setForm({ ...form, chip_number: e.target.value })} placeholder="15 dígitos" className="mt-1" style={inputStyle} />
                </div>
                <div>
                  <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>MEDICACIÓN</Label>
                  <Input value={form.medication} onChange={(e) => setForm({ ...form, medication: e.target.value })} placeholder="Ej: Insulina cada 12h" className="mt-1" style={inputStyle} />
                </div>
                <div>
                  <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>¿SE DEJA TOCAR?</Label>
                  <Select value={form.approachable} onValueChange={(v) => setForm({ ...form, approachable: v })}>
                    <SelectTrigger className="mt-1" style={selectStyle}><SelectValue /></SelectTrigger>
                    <SelectContent style={{ borderRadius: 0, border: B.border }}>
                      <SelectItem value="si">Sí, es amigable</SelectItem>
                      <SelectItem value="con_cuidado">Con cuidado, es tímido/a</SelectItem>
                      <SelectItem value="no">No, puede huir o morder</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="p-4" style={{ background: "#f8f7f4", border: "2px solid #2D3561" }}>
              <h4 className="text-xs font-bold tracking-widest mb-3" style={{ fontFamily: B.mono, color: "#8B9A2D" }}>CONTACTO</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>TELÉFONO *</Label>
                  <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="600 123 456" className="mt-1" style={inputStyle} data-testid="pet-phone-input" />
                </div>
                <div>
                  <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>EMAIL</Label>
                  <Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="tu@email.com" className="mt-1" style={inputStyle} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4" style={{ borderTop: B.border }}>
            <BBtn variant="outline" onClick={() => setShowCreate(false)}>CANCELAR</BBtn>
            <BBtn onClick={handleCreate} disabled={submitting} variant="primary" testId="submit-lost-pet-btn">
              {submitting ? "CREANDO..." : "CREAR REPORTE"}
            </BBtn>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pet Detail Dialog */}
      <Dialog open={showPoster} onOpenChange={setShowPoster}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" style={dialogStyle}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: "#e8637a", border: "2px solid #2D3561" }}>
                <Search className="w-4 h-4 text-white" />
              </div>
              {selectedPet?.name || "DETALLE"}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPet && (
            <Tabs defaultValue="poster" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4 p-0 h-auto" style={{ background: "transparent", gap: 0 }}>
                <TabsTrigger value="poster" className="py-2.5 text-[10px] font-bold uppercase tracking-wider data-[state=active]:bg-[#2D3561] data-[state=active]:text-white" style={{ fontFamily: B.mono, border: B.border, borderRadius: 0, marginRight: "-3px" }} data-testid="tab-poster">
                  <Printer className="w-3.5 h-3.5 mr-1.5" /> CARTEL
                </TabsTrigger>
                <TabsTrigger value="sightings" className="py-2.5 text-[10px] font-bold uppercase tracking-wider data-[state=active]:bg-[#2D3561] data-[state=active]:text-white" style={{ fontFamily: B.mono, border: B.border, borderRadius: 0 }} data-testid="tab-sightings">
                  <Eye className="w-3.5 h-3.5 mr-1.5" /> AVISTAMIENTOS ({sightings.length})
                </TabsTrigger>
              </TabsList>

              {/* POSTER TAB */}
              <TabsContent value="poster">
                <div ref={posterRef} className="bg-white p-6" style={{ border: "4px solid #e8637a" }}>
                  <div className="text-center mb-4">
                    <h2 className="text-3xl font-black tracking-wider" style={{ color: "#e8637a" }}>SE BUSCA</h2>
                    <p className="text-lg font-bold mt-1" style={{ color: "#2D3561" }}>
                      {SPECIES_CONFIG[selectedPet.species]?.label || "Animal"} perdido/a
                    </p>
                  </div>
                  
                  <div className="flex justify-center mb-4">
                    <img src={selectedPet.photo_url} alt={selectedPet.name} className="w-48 h-48 object-cover" style={{ border: "4px solid #2D3561" }} />
                  </div>

                  <div className="text-center mb-4">
                    <h3 className="text-2xl font-black" style={{ color: "#2D3561" }}>{selectedPet.name}</h3>
                    <p style={{ color: "#666" }}>{selectedPet.breed && `${selectedPet.breed} • `}{selectedPet.color}</p>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#e8637a" }} />
                      <span><strong>Zona:</strong> {selectedPet.lost_location_name}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#e8637a" }} />
                      <span><strong>Fecha:</strong> {new Date(selectedPet.lost_date).toLocaleDateString('es-ES')} {selectedPet.lost_time && `a las ${selectedPet.lost_time}`}</span>
                    </div>
                    {selectedPet.characteristics && (
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#e8637a" }} />
                        <span><strong>Señas:</strong> {selectedPet.characteristics}</span>
                      </div>
                    )}
                    <div className="flex items-start gap-2">
                      <Heart className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: APPROACHABLE_CONFIG[selectedPet.approachable]?.color }} />
                      <span><strong>Aproximarse:</strong> {APPROACHABLE_CONFIG[selectedPet.approachable]?.label}</span>
                    </div>
                    {(selectedPet.medication || selectedPet.health_issues) && (
                      <div className="flex items-start gap-2 p-2" style={{ background: "#fa900015", border: "2px solid #fa9000" }}>
                        <Pill className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#fa9000" }} />
                        <span style={{ color: "#fa9000" }}><strong>Salud:</strong> {selectedPet.medication || selectedPet.health_issues}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4" style={{ borderTop: "2px dashed #ccc" }}>
                    <div className="flex items-center justify-center gap-4 text-lg font-bold" style={{ color: "#2D3561" }}>
                      <Phone className="w-5 h-5" style={{ color: "#e8637a" }} />
                      {selectedPet.contact_phone}
                    </div>
                    {selectedPet.contact_email && <p className="text-center text-sm mt-1" style={{ color: "#999" }}>{selectedPet.contact_email}</p>}
                  </div>

                  <p className="text-center text-xs mt-4" style={{ color: "#ccc" }}>emergenciaanimal.conecta</p>
                </div>

                {/* Poster Actions */}
                <div className="flex flex-wrap gap-3 mt-4">
                  <BBtn onClick={downloadPoster} variant="secondary" testId="download-poster-btn" className="flex-1">
                    <Download className="w-4 h-4" /> DESCARGAR
                  </BBtn>
                  <ShareLostPetButton pet={selectedPet} className="flex-1" />
                  <SpeakButton
                    text={`Se busca a ${selectedPet.name}. ${SPECIES_CONFIG[selectedPet.species]?.label || 'Animal'} perdido. ${selectedPet.breed ? `Raza ${selectedPet.breed}.` : ''} Perdido en ${selectedPet.lost_location_name}. Teléfono: ${selectedPet.contact_phone}.`}
                    label="Escuchar"
                    className="flex-1"
                  />
                  {user && selectedPet.user_id === user.user_id && selectedPet.status === "perdido" && (
                    <BBtn onClick={() => markAsFound(selectedPet.pet_id)} variant="success" testId="mark-found-btn" className="flex-1">
                      <CheckCircle className="w-4 h-4" /> ENCONTRADO
                    </BBtn>
                  )}
                </div>

                {/* Telegram Button - Admin Only */}
                {isAdmin && selectedPet.status === "perdido" && (
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => publishToTelegram(selectedPet.pet_id)}
                      disabled={selectedPet.telegram_published}
                      className={`flex-1 py-3 flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider transition-all duration-150 ${selectedPet.telegram_published ? 'opacity-60' : 'hover:-translate-y-0.5'}`}
                      style={{
                        fontFamily: B.mono,
                        background: selectedPet.telegram_published ? "#0088cc30" : "#0088cc",
                        color: selectedPet.telegram_published ? "#0088cc" : "#fff",
                        border: "3px solid #0088cc",
                        boxShadow: selectedPet.telegram_published ? "none" : B.shadowSm,
                      }}
                      data-testid="telegram-publish-btn"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
                      </svg>
                      {selectedPet.telegram_published ? "TELEGRAM ✓" : "TELEGRAM"}
                    </button>
                    <button
                      onClick={() => publishToWhatsApp(selectedPet)}
                      className="flex-1 py-3 flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider transition-all duration-150 hover:-translate-y-0.5"
                      style={{
                        fontFamily: B.mono,
                        background: "#25D366",
                        color: "#fff",
                        border: "3px solid #25D366",
                        boxShadow: B.shadowSm,
                      }}
                      data-testid="whatsapp-publish-btn"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WHATSAPP
                    </button>
                  </div>
                )}

                <div className="mt-4 pt-4" style={{ borderTop: "2px solid #f8f7f4" }}>
                  <p className="text-[10px] tracking-widest mb-3 text-center" style={{ fontFamily: B.mono, color: "#a88efb" }}>// COMPARTE DIRECTAMENTE EN:</p>
                  <ShareButtonsRow
                    text={`SE BUSCA: ${selectedPet.name}\n${SPECIES_CONFIG[selectedPet.species]?.label || 'Animal'}${selectedPet.breed ? ` - ${selectedPet.breed}` : ''}\n${selectedPet.lost_location_name}\nContacto: ${selectedPet.contact_phone}`}
                    url={`${window.location.origin}/lost-pets?pet=${selectedPet.pet_id}`}
                    size="md"
                    className="justify-center"
                    shareType="lost_pet"
                    resourceId={selectedPet.pet_id}
                  />
                </div>
              </TabsContent>

              {/* SIGHTINGS TAB */}
              <TabsContent value="sightings">
                <div className="space-y-4">
                  {selectedPet.status === "perdido" && (
                    <BBtn onClick={() => user ? setShowSighting(true) : navigate("/auth")} variant="primary" className="w-full" testId="report-sighting-btn">
                      <Eye className="w-4 h-4" /> HE VISTO A ESTE ANIMAL
                    </BBtn>
                  )}

                  {sightings.length > 0 && <SightingsMap pet={selectedPet} sightings={sightings} />}

                  {sightings.length === 0 ? (
                    <BCard className="text-center py-8">
                      <Eye className="w-10 h-10 mx-auto mb-3" style={{ color: "#ccc" }} />
                      <p className="text-sm font-bold" style={{ color: "#2D3561" }}>AÚN NO HAY AVISTAMIENTOS</p>
                      <p className="text-[10px] tracking-widest mt-1" style={{ fontFamily: B.mono, color: "#a88efb" }}>
                        // SI VES A {selectedPet.name.toUpperCase()}, REPÓRTALO
                      </p>
                    </BCard>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-[10px] tracking-widest" style={{ fontFamily: B.mono, color: "#a88efb" }}>
                        // {sightings.length} AVISTAMIENTO{sightings.length !== 1 ? "S" : ""} REPORTADO{sightings.length !== 1 ? "S" : ""}
                      </p>
                      {sightings.map((s) => (
                        <SightingCard key={s.sighting_id} sighting={s} user={user} onReport={handleReportSighting} />
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Sighting Dialog */}
      <Dialog open={showSighting} onOpenChange={setShowSighting}>
        <DialogContent className="max-w-md" style={dialogStyle}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: "#686af3", border: "2px solid #2D3561" }}>
                <Eye className="w-4 h-4 text-white" />
              </div>
              REPORTAR AVISTAMIENTO
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div className="p-3" style={{ background: "#686af315", border: "2px solid #686af3" }}>
              <p className="text-xs" style={{ fontFamily: B.mono, color: "#686af3" }}>
                Describe lo que viste con el mayor detalle posible. ¡Esto ayuda enormemente!
              </p>
            </div>

            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>¿QUÉ VISTE? *</Label>
              <Textarea value={sightingForm.description} onChange={(e) => setSightingForm({ ...sightingForm, description: e.target.value })} placeholder="Vi un animal parecido corriendo por..." className="mt-1" style={inputStyle} data-testid="sighting-description-input" />
            </div>

            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>UBICACIÓN *</Label>
              <div className="flex gap-2 mt-1">
                <Input value={sightingForm.location_name} onChange={(e) => setSightingForm({ ...sightingForm, location_name: e.target.value })} placeholder="Nombre del lugar" className="flex-1" style={inputStyle} data-testid="sighting-location-input" />
                <button type="button" onClick={geolocateSighting} disabled={locating} className="w-10 h-10 flex items-center justify-center transition-colors hover:bg-[#686af315]" style={{ border: "2px solid #686af3" }} data-testid="sighting-geolocate-btn">
                  <Navigation className={`w-4 h-4 ${locating ? "animate-spin" : ""}`} style={{ color: "#686af3" }} />
                </button>
              </div>
              {sightingForm.location_lat && (
                <p className="text-xs mt-1 flex items-center gap-1" style={{ color: "#8B9A2D" }}>
                  <CheckCircle className="w-3 h-3" /> Ubicación registrada
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>FOTO (OPCIONAL)</Label>
              <Input value={sightingForm.photo_url} onChange={(e) => setSightingForm({ ...sightingForm, photo_url: e.target.value })} placeholder="URL de la foto" className="mt-1" style={inputStyle} />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2" style={{ borderTop: "2px solid #f8f7f4" }}>
            <BBtn variant="outline" onClick={() => setShowSighting(false)}>CANCELAR</BBtn>
            <BBtn onClick={handleCreateSighting} disabled={submitting} variant="secondary" testId="submit-sighting-btn">
              {submitting ? "ENVIANDO..." : "REPORTAR"}
            </BBtn>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===== MAP COMPONENTS =====
function HeatLayer({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    const heat = L.heatLayer(points, {
      radius: 35, blur: 25, maxZoom: 17, minOpacity: 0.4,
      gradient: { 0.2: "#686af3", 0.4: "#a88efb", 0.6: "#fa9000", 0.8: "#f64e0e", 1.0: "#e8637a" },
    }).addTo(map);
    return () => { map.removeLayer(heat); };
  }, [map, points]);
  return null;
}

const lostIcon = new L.DivIcon({
  html: '<div style="background:#e8637a;width:16px;height:16px;border:3px solid #2D3561;box-shadow:2px 2px 0 #2D3561"></div>',
  iconSize: [16, 16], iconAnchor: [8, 8], className: "",
});
const sightingIcon = new L.DivIcon({
  html: '<div style="background:#686af3;width:12px;height:12px;border:2px solid #2D3561;box-shadow:2px 2px 0 #2D3561"></div>',
  iconSize: [12, 12], iconAnchor: [6, 6], className: "",
});
const verifiedIcon = new L.DivIcon({
  html: '<div style="background:#8B9A2D;width:14px;height:14px;border:2px solid #2D3561;box-shadow:2px 2px 0 #2D3561"></div>',
  iconSize: [14, 14], iconAnchor: [7, 7], className: "",
});

function SightingsMap({ pet, sightings }) {
  const lostLat = pet.lost_location_lat;
  const lostLng = pet.lost_location_lng;
  const hasPetLocation = lostLat && lostLng;
  const validSightings = sightings.filter(s => s.location_lat && s.location_lng);
  const heatPoints = [];
  if (hasPetLocation) heatPoints.push([lostLat, lostLng, 0.8]);
  validSightings.forEach(s => heatPoints.push([s.location_lat, s.location_lng, 1.0]));
  const allLats = heatPoints.map(p => p[0]);
  const allLngs = heatPoints.map(p => p[1]);
  const center = heatPoints.length > 0 ? [(Math.min(...allLats) + Math.max(...allLats)) / 2, (Math.min(...allLngs) + Math.max(...allLngs)) / 2] : [40.416, -3.703];

  if (heatPoints.length === 0) {
    return (
      <BCard className="text-center py-6">
        <Map className="w-10 h-10 mx-auto mb-2" style={{ color: "#ccc" }} />
        <p className="text-sm" style={{ color: "#999" }}>No hay ubicaciones para mostrar</p>
      </BCard>
    );
  }

  return (
    <div className="overflow-hidden" style={{ border: B.border }} data-testid="sightings-map">
      <div className="px-4 py-2 flex items-center gap-2" style={{ background: "#2D3561" }}>
        <Map className="w-4 h-4 text-white" />
        <span className="text-xs font-bold tracking-widest text-white" style={{ fontFamily: B.mono }}>MAPA</span>
        <div className="flex items-center gap-3 ml-auto text-[9px]">
          <span className="flex items-center gap-1"><span className="w-2 h-2 inline-block" style={{ background: "#e8637a" }} /><span className="text-white/70">Perdido</span></span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 inline-block" style={{ background: "#686af3" }} /><span className="text-white/70">Avistamiento</span></span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 inline-block" style={{ background: "#8B9A2D" }} /><span className="text-white/70">Verificado</span></span>
        </div>
      </div>
      <MapContainer center={center} zoom={13} style={{ height: "280px", width: "100%" }} className="z-0">
        <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" attribution='&copy; OSM &copy; CARTO' />
        <HeatLayer points={heatPoints} />
        {hasPetLocation && <Marker position={[lostLat, lostLng]} icon={lostIcon}><Popup><div className="text-center p-1"><p className="font-bold text-xs" style={{ color: "#e8637a" }}>Donde se perdió</p><p className="text-xs">{pet.lost_location_name}</p></div></Popup></Marker>}
        {validSightings.map((s) => (<Marker key={s.sighting_id} position={[s.location_lat, s.location_lng]} icon={s.status === "verificado" ? verifiedIcon : sightingIcon}><Popup><div className="p-1 max-w-[200px]"><p className="font-bold text-xs" style={{ color: "#686af3" }}>{s.user_name}</p><p className="text-xs mt-0.5">{s.description}</p>{s.status === "verificado" && <p className="text-[10px] font-semibold mt-0.5" style={{ color: "#8B9A2D" }}>Verificado</p>}</div></Popup></Marker>))}
        {hasPetLocation && <Circle center={[lostLat, lostLng]} radius={2000} pathOptions={{ color: "#e8637a", fillColor: "#e8637a", fillOpacity: 0.05, weight: 2, dashArray: "5,5" }} />}
      </MapContainer>
    </div>
  );
}

// ===== PET CARD =====
function PetCard({ pet, onOpenPoster }) {
  const speciesConfig = SPECIES_CONFIG[pet.species] || SPECIES_CONFIG.otro;
  const SpeciesIcon = speciesConfig.icon;
  const approachable = APPROACHABLE_CONFIG[pet.approachable];
  const isFound = pet.status === "encontrado";

  return (
    <BCard className={`overflow-hidden transition-all hover:-translate-y-1 ${isFound ? 'opacity-70' : ''}`} data-testid={`pet-card-${pet.pet_id}`}>
      <div className="relative">
        <img src={pet.photo_url} alt={pet.name} className="w-full h-48 object-cover" style={{ borderBottom: B.border }} onError={(e) => { e.target.src = 'https://placehold.co/400x300/f3f4f6/9ca3af?text=Sin+foto'; }} />
        <div className="absolute top-3 left-3 flex gap-2">
          <BBadge bg={speciesConfig.color}><SpeciesIcon className="w-3 h-3" />{speciesConfig.label}</BBadge>
          {isFound && <BBadge bg="#8B9A2D"><CheckCircle className="w-3 h-3" />ENCONTRADO</BBadge>}
        </div>
        <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-lg" style={{ background: `${approachable?.color}30`, border: "2px solid #2D3561" }} title={approachable?.label}>
          {approachable?.icon}
        </div>
      </div>

      <div className="p-4">
        <h3 className="text-lg font-bold" style={{ color: "#2D3561" }}>{pet.name}</h3>
        <p className="text-sm" style={{ color: "#999" }}>{pet.breed && `${pet.breed} • `}{pet.color}</p>

        <div className="flex items-center gap-1 mt-2 text-sm" style={{ color: "#666" }}>
          <MapPin className="w-3 h-3" style={{ color: "#e8637a" }} />
          <span className="truncate">{pet.lost_location_name}</span>
        </div>
        <div className="flex items-center gap-1 text-sm" style={{ color: "#666" }}>
          <Calendar className="w-3 h-3" style={{ color: "#e8637a" }} />
          <span>{new Date(pet.lost_date).toLocaleDateString('es-ES')}</span>
        </div>

        <div className="flex gap-2 mt-4">
          <BBtn onClick={() => onOpenPoster(pet)} variant="primary" testId={`pet-poster-btn-${pet.pet_id}`} className="flex-1" style={{ fontSize: "10px", padding: "8px 12px" }}>
            VER CARTEL
          </BBtn>
          <a href={`tel:${pet.contact_phone}`} className="w-9 h-9 flex items-center justify-center transition-all hover:-translate-y-0.5" style={{ background: "#8B9A2D", border: B.border, boxShadow: B.shadowSm }}>
            <Phone className="w-4 h-4 text-white" />
          </a>
        </div>
      </div>
    </BCard>
  );
}

// ===== SIGHTING CARD =====
function SightingCard({ sighting, user, onReport }) {
  const timeAgo = (date) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `hace ${mins}min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `hace ${hrs}h`;
    return `hace ${Math.floor(hrs / 24)}d`;
  };

  const isVerified = sighting.status === "verificado";
  const hasReported = user && sighting.reported_by?.includes(user.user_id);

  return (
    <BCard className={`p-4`} style={isVerified ? { background: "#8B9A2D15", borderColor: "#8B9A2D" } : {}} data-testid={`sighting-card-${sighting.sighting_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <div className="w-7 h-7 flex items-center justify-center shrink-0" style={{ background: "#686af3", border: "2px solid #2D3561" }}>
              <Eye className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-sm font-bold truncate" style={{ color: "#2D3561" }}>{sighting.user_name}</span>
            {isVerified && <BBadge bg="#8B9A2D"><CheckCircle className="w-2.5 h-2.5" />VERIFICADO</BBadge>}
            <span className="text-[10px] shrink-0" style={{ fontFamily: B.mono, color: "#a88efb" }}>{timeAgo(sighting.created_at)}</span>
          </div>

          <p className="text-sm ml-9" style={{ color: "#666" }}>{sighting.description}</p>

          <div className="flex items-center gap-3 ml-9 mt-2 text-[10px]" style={{ fontFamily: B.mono, color: "#999" }}>
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" style={{ color: "#e8637a" }} />{sighting.location_name || "Ubicación registrada"}</span>
          </div>

          {sighting.photo_url && <img src={sighting.photo_url} alt="Avistamiento" className="ml-9 mt-2 w-32 h-24 object-cover" style={{ border: "2px solid #2D3561" }} />}
        </div>

        {user && user.user_id !== sighting.user_id && !hasReported && (
          <button onClick={() => onReport(sighting.sighting_id)} className="shrink-0 p-1.5 transition-colors hover:bg-[#e8637a15]" style={{ border: "2px solid #e8637a" }} title="Reportar como falso" data-testid={`report-sighting-${sighting.sighting_id}`}>
            <Flag className="w-3.5 h-3.5" style={{ color: "#e8637a" }} />
          </button>
        )}
      </div>

      {sighting.report_count > 0 && (
        <div className="mt-2 ml-9 text-[10px] flex items-center gap-1" style={{ fontFamily: B.mono, color: "#fa9000" }}>
          <AlertTriangle className="w-3 h-3" />
          {sighting.report_count} reporte{sighting.report_count !== 1 ? "s" : ""} de la comunidad
        </div>
      )}
    </BCard>
  );
}
